<template>
  <svg
    v-bind="{ ...svgAttributes, ...$attrs }"
    :style="{
      transform: zoom !== 1 ? `scale(${zoom})` : undefined,
      padding: typeof padding === 'number' ? `${padding}px` : padding,
    }"
    v-html="svgChildren"
  />
</template>

<script>
import useState from '@front/common/hooks/useState'
import { defineComponent, toRefs, watch } from 'vue-demi'

export default defineComponent({
  name: 'Icon', // @TODO: remove name when vue-devtools handles it automatically
  props: {
    name: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'regular',
      validator: (value) => ['regular', 'solid'].includes(value),
    },
    url: {
      type: String,
      default: '',
    },
    zoom: {
      type: [Number, String],
      default: 1,
    },
    padding: {
      type: [Number, String],
      default: 0,
    },
  },
  setup(props) {
    const { name, url } = toRefs(props)

    const [svgAttributes, setAttributes] = useState(null)
    const [svgChildren, setChildren] = useState(null)

    async function getIcon() {
      if (props.url) {
        try {
          const response = await fetch(props.url)

          if (!response.ok) {
            throw new Error(`Failed to fetch SVG: ${response.statusText}`)
          }
          return await response.text()
        } catch (error) {
          console.error('Error fetching SVG from URL:', error)
          return ''
        }
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires, import/no-dynamic-require, global-require
        const icon = require(`!!svg-inline-loader?!../assets/${name.value}.svg`)

        return icon
      } catch (error) {
        console.error(error)
        return ''
      }
    }

    function parseSvg(svg) {
      // Extract the main SVG element
      const svgMatch = svg.match(/<svg\s[^>]*>/i)

      if (!svgMatch) return [null, null] // If no SVG element is found, return null

      const svgTag = svgMatch[0]

      // Extract the attributes of the SVG element
      const attributeRegex =
        /(\w+)=["']?((?:.(?!["']?\s+(?:\S+)=|\s*\/?[>"']))+.)["']?/g
      const attributesObject = {}
      let match

      // eslint-disable-next-line no-cond-assign
      while ((match = attributeRegex.exec(svgTag))) {
        // eslint-disable-next-line prefer-destructuring
        attributesObject[match[1]] = match[2]
      }

      // Extract the children of the SVG element
      const childrenRegex = /<svg\s[^>]*>([\s\S]*?)<\/svg>/i
      const childrenMatch = svg.match(childrenRegex)
      const childrenArray = childrenMatch ? childrenMatch[1].trim() : ''

      return [attributesObject, childrenArray]
    }

    async function generateIcon() {
      try {
        const icon = await getIcon()
        const [attributes, children] = parseSvg(icon)

        setAttributes(attributes)
        setChildren(children)
      } catch (error) {
        console.error(error)
      }
    }

    generateIcon()

    watch([name, url], generateIcon)

    return { svgChildren, svgAttributes }
  },
})
</script>
